import { useEffect, useState } from "react";
import { Gift, Lock } from "lucide-react";
import { api } from "@/lib/api";
import DailySpinWheel from "@/components/DailySpinWheel";
import ScratchCard from "@/components/ScratchCard";
import BallChallenge from "@/components/BallChallenge";
import { CoinIcon } from "@/components/CoinBadge";

const ART = {
  spin: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/cd863bc5ad66288e08ab4c21cc04d98bd46795388cd5c70d2a7635d748a7b1ce.jpeg",
  scratch: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/d820a46676e1afc5dd864214efd1f689cecd60ec17b5cd3d63115b23cda7cce5.jpeg",
  ball: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/94424d569117741bc67b3ed09383c487666d64e2282c10658b135afdbe3cce72.jpeg",
  quiz: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/09183e27b49df74d04f403fefb9412517f68384ce4a297f2fcdcac4d143882df.jpeg",
  draw: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/91f7270fc5e92d594eebfa8aac1f37b550306b39a39fe6b8d793d76565f72618.jpeg",
  weekly: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/4db73324f07d8db398672049e96db687b210e2afc8e108065c9e536a8c8d2b28.jpeg",
  monthly: "https://static.prod-images.emergentagent.com/jobs/a288c9ba-7e69-49cf-a463-20e04b359c08/images/af57b249ef70430ad1005e3a57a1af6b3e756962ee7749c10d43577d8b6a0324.jpeg",
};

const LIVE = [
  { key: "spin", name: "Daily Spin", desc: "Spin once a day for bonus ZRo Pe Coins" },
  { key: "scratch", name: "Scratch Card", desc: "Scratch to reveal ZRo Pe Coins" },
  { key: "ball", name: "Ball Challenge", desc: "Beat the penguin keeper to score" },
];
const SOON = [
  { key: "quiz", name: "Daily Quiz", desc: "Answer finance questions" },
  { key: "draw", name: "Lucky Draw", desc: "Enter the weekly draw" },
  { key: "weekly", name: "Weekly Challenge", desc: "Pay 3 EMIs on time" },
  { key: "monthly", name: "Monthly Challenge", desc: "Stay on track all month" },
];

function GameCard({ g, live, onPlay }) {
  return (
    <div data-testid={`game-card-${g.key}`} className={`group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-300 ${live ? "border-[#14B8A6]/30 ring-1 ring-[#14B8A6]/10 hover:-translate-y-1 hover:shadow-lg" : "border-slate-200"}`}>
      <div className="relative h-40 overflow-hidden">
        <img src={ART[g.key]} alt={g.name} loading="lazy"
          className={`h-full w-full object-cover transition-transform duration-500 ${live ? "group-hover:scale-105" : "scale-100 blur-[2px] grayscale-[35%] brightness-[0.8]"}`} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,19,43,0.35), transparent 55%)" }} />
        {live ? (
          <span className="absolute left-3 top-3 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white shadow" data-testid="badge-live">Live</span>
        ) : (
          <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur-sm"><Lock size={11} /> Coming soon</span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="font-display text-base font-bold text-[#0B132B]">{g.name}</p>
        <p className="mt-1 flex-1 text-sm text-slate-500">{g.desc}</p>
        {live ? (
          <button onClick={onPlay} data-testid={`play-now-${g.key}`}
            className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#14B8A6] to-[#0D9488] px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md active:scale-[0.98]">
            <Gift size={16} /> Play now
          </button>
        ) : (
          <button disabled data-testid={`coming-soon-${g.key}`}
            className="mt-3 cursor-not-allowed rounded-xl bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-400">Coming soon</button>
        )}
      </div>
    </div>
  );
}

export default function Games() {
  const [coins, setCoins] = useState(null);
  const [active, setActive] = useState(null); // 'spin' | 'scratch' | 'ball'

  const loadCoins = () => api.get("/rewards").then((r) => setCoins(r.data.points)).catch(() => setCoins(0));
  useEffect(() => { loadCoins(); }, []);

  return (
    <div className="space-y-6">
      <div className="relative mx-auto w-full max-w-[900px]" style={{ containerType: "inline-size" }}>
        <img src="/brand/penguin-play-earn.webp" alt="Play & Earn — play games and earn ZRo Pe Coins" className="block h-auto w-full" />
        <div className="absolute flex items-center justify-center text-center" style={{ left: "45.5%", top: "28%", width: "21%", height: "23%" }} data-testid="coins-badge">
          <span className="font-display font-bold text-[#0F9A6C]" style={{ fontSize: "clamp(14px,5cqw,44px)", lineHeight: 1 }}>{coins === null ? "…" : coins.toLocaleString("en-IN")}</span>
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#14B8A6]">Live now</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {LIVE.map((g) => <GameCard key={g.key} g={g} live onPlay={() => setActive(g.key)} />)}
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">Coming soon</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SOON.map((g) => <GameCard key={g.key} g={g} live={false} />)}
        </div>
      </div>

      <p className="text-xs text-slate-400">Coin balances can never be modified from the client — every reward is validated and credited on the server. ZRo Pe Coins are non-withdrawable reward credits and can only be used toward eligible payments.</p>

      <DailySpinWheel open={active === "spin"} onClose={() => { setActive(null); loadCoins(); }} onPointsChange={setCoins} />
      <ScratchCard open={active === "scratch"} onClose={() => { setActive(null); loadCoins(); }} onPointsChange={setCoins} />
      <BallChallenge open={active === "ball"} onClose={() => { setActive(null); loadCoins(); }} onPointsChange={setCoins} />
    </div>
  );
}
